// ─────────────────────────────────────────────────────────────────────────────
// The form control.
//
// Anywhere a form needs a file, this is the field: it uploads to S3 through the
// media service and hands back a value whose `url` can be stored as it stands.
//
//   const [header, setHeader] = useState<MediaValue | null>(null);
//   <FileUpload value={header} onChange={setHeader} accept={FileUpload.ACCEPT.IMAGE} />
//
// A column that can only hold a string works too:
//
//   <FileUpload value={url} onChange={(v) => setUrl(v?.url ?? '')} />
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { media } from '@repletoryreach/sdk-api';
import { Button, Stack, Group, Text, Progress, ActionIcon } from '@repletoryreach/sdk-ui';
import { Dropzone } from '@repletoryreach/sdk-ui/dropzone';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { openFileViewer } from '@repletoryreach/sdk-ui/file-viewer';
import { mediaViewerSource } from './viewer.js';
import { MediaThumb } from './MediaPreview.js';
import { MediaLibrary } from './MediaLibrary.js';
import { describe, useMediaUpload, type UploadItem } from './useMediaUpload.js';
import {
  DEFAULT_LABELS, formatBytes, normaliseValue, toMediaValue,
  type FileUploadLabels, type MediaValue
} from './types.js';

export interface FileUploadProps {
  /** A `MediaValue`, or a bare URL for a column that only holds a string. */
  value?: MediaValue | string | null;
  /** Null when the field is cleared. */
  onChange: (value: MediaValue | null) => void;
  /** MIME types, extensions (`'.pdf'`) or wildcards (`'image/*'`). */
  accept?: string[];
  /** Client-side ceiling in bytes. The service enforces its own as well. */
  maxSize?: number;
  /** Take several files. `onChange` fires with the last; use `onUploaded` for all. */
  multiple?: boolean;
  /** Every completed upload, including in multiple mode. */
  onUploaded?: (value: MediaValue, file: File) => void;
  /** Offer the tenant's existing files. Default true. */
  withLibrary?: boolean;
  /** Offer rename and permanent delete on the chosen file. Default true. */
  withManagement?: boolean;
  disabled?: boolean;
  labels?: Partial<FileUploadLabels>;
  /** Marks the field invalid, matching the sdk-ui inputs. */
  invalid?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * A signed URL younger than this is treated as fresh. Storing one and opening
 * it an hour later is the normal case, so the field re-signs on mount when the
 * value it was given is close to expiring.
 */
const REFRESH_WITHIN_MS = 5 * 60 * 1000;

function FileUploadBase({
  value, onChange, accept, maxSize, multiple = false, onUploaded,
  withLibrary = true, withManagement = true, disabled, labels, invalid,
  className, 'data-testid': testId
}: FileUploadProps) {
  const L = { ...DEFAULT_LABELS, ...labels };
  const current = normaliseValue(value);

  const [libraryOpen, setLibraryOpen] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { items, add, cancel, retry, dismiss } = useMediaUpload({
    labels,
    maxSize,
    onUploaded: (v, file) => {
      onChange(v);
      onUploaded?.(v, file);
    },
    onError: setError
  });

  /*
   * Re-sign a stored URL that is about to expire. A template saved last month
   * carries a URL that 403s, and the picture silently vanishing from the form
   * looks like data loss rather than an expired signature.
   */
  useEffect(() => {
    if (!current?.id || !current.url_expires_at) return;
    const msLeft = new Date(current.url_expires_at).getTime() - Date.now();
    if (msLeft > REFRESH_WITHIN_MS) return;
    let live = true;
    media.get(current.id)
      .then((res) => { if (live) onChange(toMediaValue(res.data)); })
      .catch(() => { /* leave the stale URL: the preview falls back to an icon */ });
    return () => { live = false; };
    // Keyed on the identity of the value, not the callback, so a parent with an
    // inline `onChange` does not re-sign on every render.
  }, [current?.id, current?.url_expires_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const onReject = (rejections: Array<{ file: File; reasons: string[] }>) => {
    const reason = rejections[0]?.reasons[0];
    setError(reason === 'file-too-large' ? L.errorTooLarge : L.errorType);
  };

  const copy = async () => {
    if (!current?.url) return;
    await navigator.clipboard.writeText(current.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const rename = async (next: string) => {
    if (!current?.id || !next.trim()) { setRenaming(null); return; }
    try {
      await media.rename(current.id, next.trim());
      onChange({ ...current, original_name: next.trim() });
    } catch (e) {
      setError(describe(e, labels));
    } finally {
      setRenaming(null);
    }
  };

  const destroy = () => {
    if (!current?.id) { onChange(null); return; }
    openConfirmModal({
      title: L.deleteFile,
      children: L.deleteConfirm,
      labels: { confirm: L.deleteFile, cancel: L.cancel },
      confirmProps: { color: 'error' },
      onConfirm: () => {
        media.remove(current.id)
          .then(() => onChange(null))
          .catch((e: unknown) => setError(describe(e, labels)));
      }
    });
  };

  const inFlight = items.filter((i) => i.status !== 'done');
  const showDrop = !current || multiple;

  return (
    <Stack gap="xs" className={['rr-fileupload', invalid && 'rr-fileupload--invalid', className].filter(Boolean).join(' ')} data-testid={testId}>
      {current && (
        <div className="rr-fileupload__current">
          <MediaThumb value={current} />
          <div className="rr-fileupload__meta">
            <span className="rr-fileupload__name">{current.original_name ?? current.url}</span>
            <span className="rr-fileupload__sub">
              {[current.mime_type, formatBytes(current.size_bytes)].filter(Boolean).join(' · ')}
            </span>
          </div>
          <Group gap={4} className="rr-fileupload__actions">
            <ActionIcon variant="subtle" size="sm" onClick={() => void copy()} aria-label={L.copy} title={copied ? L.copied : L.copy}>
              {copied ? <CheckIcon /> : <LinkIcon />}
            </ActionIcon>
            {current.url && (
              // Opens in the file viewer, which renders the file in place and
              // carries Open externally and Download of its own.
              <ActionIcon variant="subtle" size="sm" onClick={() => openFileViewer(mediaViewerSource(current))} aria-label={L.view} title={L.view}>
                <OpenIcon />
              </ActionIcon>
            )}
            {withManagement && current.id && (
              <ActionIcon variant="subtle" size="sm" onClick={() => setRenaming(current.original_name ?? '')} aria-label={L.rename} title={L.rename}>
                <PencilIcon />
              </ActionIcon>
            )}
            <ActionIcon variant="subtle" size="sm" disabled={disabled} onClick={() => onChange(null)} aria-label={L.remove} title={L.remove}>
              <CloseIcon />
            </ActionIcon>
            {withManagement && current.id && (
              <ActionIcon variant="subtle" size="sm" color="error" disabled={disabled} onClick={destroy} aria-label={L.deleteFile} title={L.deleteFile}>
                <TrashIcon />
              </ActionIcon>
            )}
          </Group>
        </div>
      )}

      {showDrop && (
        <Dropzone
          onDrop={add}
          onReject={onReject}
          accept={accept}
          maxSize={maxSize}
          multiple={multiple}
          disabled={disabled}
        >
          <Stack gap={4} align="center">
            <UploadIcon />
            <Text role="bodySmall">{L.prompt}</Text>
            {L.hint && <Text role="caption" c="dimmed">{L.hint}</Text>}
          </Stack>
        </Dropzone>
      )}

      {(withLibrary || (current && !multiple)) && (
        <Group gap="xs">
          {current && !multiple && (
            <Button variant="subtle" size="xs" disabled={disabled} onClick={() => onChange(null)}>
              {L.replace}
            </Button>
          )}
          {withLibrary && (
            <Button variant="subtle" size="xs" disabled={disabled} onClick={() => setLibraryOpen(true)}>
              {L.library}
            </Button>
          )}
        </Group>
      )}

      {inFlight.map((item) => (
        <UploadRow key={item.key} item={item} labels={L} onCancel={cancel} onRetry={retry} onDismiss={dismiss} />
      ))}

      {error && <Alert color="error" role="alert" onClose={() => setError(null)} withCloseButton>{error}</Alert>}

      <Modal opened={libraryOpen} onClose={() => setLibraryOpen(false)} title={L.libraryTitle} size="lg">
        <MediaLibrary
          labels={labels}
          mime={acceptToMimePrefix(accept)}
          allowDelete={withManagement}
          onSelect={(picked) => {
            onChange(picked);
            setLibraryOpen(false);
          }}
        />
      </Modal>

      <Modal opened={renaming !== null} onClose={() => setRenaming(null)} title={L.renameTitle} size="sm">
        <form
          onSubmit={(e) => { e.preventDefault(); void rename(renaming ?? ''); }}
        >
          <Stack gap="sm">
            <TextInput
              value={renaming ?? ''}
              onChange={(e) => setRenaming(e.currentTarget.value)}
              placeholder={L.renamePlaceholder}
              aria-label={L.renamePlaceholder}
              autoFocus
            />
            <Group gap="xs" justify="flex-end">
              <Button variant="subtle" size="sm" type="button" onClick={() => setRenaming(null)}>{L.cancel}</Button>
              <Button size="sm" type="submit">{L.save}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}

function UploadRow({
  item, labels, onCancel, onRetry, onDismiss
}: {
  item: UploadItem;
  labels: FileUploadLabels;
  onCancel: (key: string) => void;
  onRetry: (key: string) => void;
  onDismiss: (key: string) => void;
}) {
  return (
    <div className="rr-fileupload__row">
      <div className="rr-fileupload__meta">
        <span className="rr-fileupload__name">{item.file.name}</span>
        {item.status === 'uploading' && (
          <>
            <Progress value={item.percent} size="xs" />
            <span className="rr-fileupload__sub">{labels.uploading} {item.percent}%</span>
          </>
        )}
        {item.status === 'error' && <span className="rr-fileupload__error">{item.error}</span>}
        {item.status === 'cancelled' && <span className="rr-fileupload__sub">{labels.cancel}</span>}
      </div>
      <Group gap={4}>
        {item.status === 'uploading' && (
          <Button variant="subtle" size="xs" onClick={() => onCancel(item.key)}>{labels.cancel}</Button>
        )}
        {(item.status === 'error' || item.status === 'cancelled') && (
          <>
            <Button variant="subtle" size="xs" onClick={() => onRetry(item.key)}>{labels.retry}</Button>
            <ActionIcon variant="subtle" size="sm" onClick={() => onDismiss(item.key)} aria-label={labels.remove}>
              <CloseIcon />
            </ActionIcon>
          </>
        )}
      </Group>
    </div>
  );
}

/**
 * Narrow the library to what the field would accept. Only a single-family
 * accept list can be expressed as one prefix, which covers the common cases
 * (an image header, a document attachment); a mixed list shows everything
 * rather than hiding files the field would have taken.
 */
function acceptToMimePrefix(accept?: string[]): string | undefined {
  if (!accept?.length) return undefined;
  const families = new Set(
    accept
      .filter((a) => a.includes('/'))
      .map((a) => `${a.split('/')[0]}/`)
  );
  return families.size === 1 ? [...families][0] : undefined;
}

const ICON = {
  width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none',
  stroke: 'currentColor', strokeWidth: 1.6,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const
};

function UploadIcon() {
  return (
    <svg {...ICON} width="22" height="22" viewBox="0 0 20 20" aria-hidden>
      <path d="M10 13V3.6M6.6 6.9L10 3.5l3.4 3.4" />
      <path d="M3.2 12.4v2.4a1.6 1.6 0 001.6 1.6h10.4a1.6 1.6 0 001.6-1.6v-2.4" />
    </svg>
  );
}
function LinkIcon() {
  return <svg {...ICON} aria-hidden><path d="M6.8 9.2a2.6 2.6 0 003.9.3l1.9-1.9a2.6 2.6 0 00-3.7-3.7l-1 1" /><path d="M9.2 6.8a2.6 2.6 0 00-3.9-.3l-1.9 1.9a2.6 2.6 0 003.7 3.7l1-1" /></svg>;
}
function CheckIcon() {
  return <svg {...ICON} aria-hidden><polyline points="3.4,8.4 6.4,11.4 12.6,4.8" /></svg>;
}
function OpenIcon() {
  return <svg {...ICON} aria-hidden><path d="M9.4 3.2h3.4v3.4M12.6 3.4L7.8 8.2" /><path d="M12.4 9.8v2.6a1.4 1.4 0 01-1.4 1.4H3.6a1.4 1.4 0 01-1.4-1.4V5a1.4 1.4 0 011.4-1.4h2.6" /></svg>;
}
function PencilIcon() {
  return <svg {...ICON} aria-hidden><path d="M11.2 2.9l1.9 1.9M3 11.1l7.3-7.3 1.9 1.9L4.9 13 2.6 13.4z" /></svg>;
}
function CloseIcon() {
  return <svg {...ICON} aria-hidden><path d="M4.2 4.2l7.6 7.6M11.8 4.2l-7.6 7.6" /></svg>;
}
function TrashIcon() {
  return <svg {...ICON} aria-hidden><path d="M2.8 4.4h10.4M6.4 4.4V3.1h3.2v1.3M4.2 4.4l.6 8.5h6.4l.6-8.5" /></svg>;
}

/** `FileUpload.ACCEPT.WHATSAPP` and friends, forwarded from the dropzone. */
export const FileUpload = Object.assign(FileUploadBase, { ACCEPT: Dropzone.ACCEPT });
